import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

const TEAM_MEMBERS = [
  'AbuZaid', 'Shabaz', 'Saad', 'Navneet', 'Ahtesham', 
  'Nadeem', 'Sohel', 'Jaffar', 'Zaid nagori'
];

const BUG_STATUS_OPTIONS = [
  'New and Created', 'Not a Bug', 'In progress', 'Resolved', 
  'Postponed', 'Duplicate', 'Reopened', 'Deployed and Closed', 
  'Explain', 'Pending'
];

const BUG_TYPE_OPTIONS = [
  'New Request', 'Change Request', 'Bug', 'Blocker'
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5];

const BugFilters = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    raisedBy: '',
    assignedTo: '',
    bugStatus: '',
    priority: '',
    type: '',
    reportToDeveloper: '',
    complaintStatus: '',
    bugFoundInVersion: '',
    dateFrom: '',
    dateTo: '',
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    
    // Remove empty filters
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([_, v]) => v !== '')
    );
    
    onFiltersChange(cleanFilters);
  };

  const clearAllFilters = () => {
    setFilters({
      raisedBy: '',
      assignedTo: '',
      bugStatus: '',
      priority: '',
      type: '',
      reportToDeveloper: '',
      complaintStatus: '',
      bugFoundInVersion: '',
      dateFrom: '',
      dateTo: '',
    });
    onFiltersChange({});
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Filters
          {activeFiltersCount > 0 && (
            <Chip 
              label={`${activeFiltersCount} active`} 
              size="small" 
              sx={{ ml: 1 }} 
            />
          )}
        </Typography>
        {activeFiltersCount > 0 && (
          <Button
            startIcon={<ClearIcon />}
            onClick={clearAllFilters}
            size="small"
          >
            Clear All
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Raised By"
            value={filters.raisedBy}
            onChange={(e) => handleFilterChange('raisedBy', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {TEAM_MEMBERS.map((member) => (
              <MenuItem key={member} value={member}>
                {member}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Assigned To"
            value={filters.assignedTo}
            onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {TEAM_MEMBERS.map((member) => (
              <MenuItem key={member} value={member}>
                {member}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Bug Status"
            value={filters.bugStatus}
            onChange={(e) => handleFilterChange('bugStatus', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {BUG_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {PRIORITY_OPTIONS.map((priority) => (
              <MenuItem key={priority} value={priority}>
                {priority}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Type"
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {BUG_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Report to Developer"
            value={filters.reportToDeveloper}
            onChange={(e) => handleFilterChange('reportToDeveloper', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {TEAM_MEMBERS.map((member) => (
              <MenuItem key={member} value={member}>
                {member}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Complaint Status"
            value={filters.complaintStatus}
            onChange={(e) => handleFilterChange('complaintStatus', e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {BUG_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Version"
            value={filters.bugFoundInVersion}
            onChange={(e) => handleFilterChange('bugFoundInVersion', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g., 1.0.0"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            label="Date From"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            label="Date To"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BugFilters;